import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, ShieldCheck, UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { RecoveryCodes } from '../components/recovery-codes.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Input,
  Label,
  Modal,
  NativeSelect,
  PageHeader,
  Spinner,
  Table,
  TD,
  TH,
} from '../components/ui.js';
import { formatDateTime } from '../lib/format.js';
import { appMetaQuery, useAppMeta } from '../lib/meta.js';
import { errorMessage, trpc, trpcClient, type RouterOutputs } from '../lib/trpc.js';

type UserSummary = RouterOutputs['users']['list'][number];
type ChangeRequest = RouterOutputs['dashboard']['changeRequests'][number];

/** The signed-in person's account, and the organization's people, exports and versions for those allowed. */
export function SettingsScreen() {
  const meta = useAppMeta();
  const can = (capability: string) => meta.capabilities.includes(capability);
  return (
    <>
      <PageHeader
        title={can('manage_users') ? 'Settings' : 'Your account'}
        subtitle={meta.organization.name}
      />
      <div className="space-y-5">
        <PasswordCard />
        <TwoFactorCard />
        <TokensCard />
        <SummaryEmailCard />
        <ChangeRequestsCard everyone={can('manage_users')} />
        {can('manage_users') && <UsersCard />}
        {can('manage_integrations') && <DashboardDataCard />}
        {can('export_data') && <ExportCard />}
        <AboutCard />
      </div>
    </>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => trpcClient.auth.changePassword.mutate({ current, next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    change.mutate();
  };
  return (
    <Card title="Password">
      <form onSubmit={submit} className="grid max-w-xl gap-4 sm:grid-cols-2">
        {change.error && (
          <div className="sm:col-span-2">
            <ErrorBanner>{errorMessage(change.error)}</ErrorBanner>
          </div>
        )}
        {change.isSuccess && (
          <p className="text-sm text-green-700 sm:col-span-2">
            Your password is changed. Other devices have been signed out.
          </p>
        )}
        <div>
          <Label htmlFor="current-password">Current password</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="next-password">New password</Label>
          <Input
            id="next-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" busy={change.isPending}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** When the person gets the dashboard's morning summary by email. Hidden where none are sent. */
function SummaryEmailCard() {
  const queryClient = useQueryClient();
  const preference = useQuery(trpc.dashboard.summaryEmail.queryOptions());
  const choose = useMutation({
    mutationFn: (choice: 'off' | 'mondays' | 'weekdays') =>
      trpcClient.dashboard.setSummaryEmail.mutate({ choice }),
    onSuccess: (data) => queryClient.setQueryData(trpc.dashboard.summaryEmail.queryKey(), data),
  });
  if (!preference.data?.sends) return null;
  return (
    <Card title="Summary email">
      <div className="max-w-xl space-y-2">
        <p className="text-sm text-slate-600">
          The top of your dashboard by email at 6:00 in the morning: your number, what needs you and
          the main figures. It only shows what you can see here.
        </p>
        {choose.error && <ErrorBanner>{errorMessage(choose.error)}</ErrorBanner>}
        <Label htmlFor="summary-email">Send it</Label>
        <NativeSelect
          id="summary-email"
          className="w-full sm:w-64"
          value={preference.data.choice}
          disabled={choose.isPending}
          onChange={(e) => choose.mutate(e.target.value as 'off' | 'mondays' | 'weekdays')}
        >
          <option value="mondays">Mondays</option>
          <option value="weekdays">Every weekday</option>
          <option value="off">Never</option>
        </NativeSelect>
      </div>
    </Card>
  );
}

const CHANGE_STATUS: Record<
  ChangeRequest['status'],
  { label: string; tone: 'neutral' | 'green' | 'amber' | 'red' }
> = {
  kept: { label: 'Kept here', tone: 'neutral' },
  sending: { label: 'Sending', tone: 'amber' },
  sent: { label: 'Sent', tone: 'green' },
  not_sent: { label: 'Couldn’t be sent', tone: 'red' },
};

/**
 * The changes asked for from the dashboard's cards: everyone's for an administrator, the
 * person's own for anyone else. Hidden until there is one.
 */
function ChangeRequestsCard({ everyone }: { everyone: boolean }) {
  const requests = useQuery(trpc.dashboard.changeRequests.queryOptions());
  if (!requests.data?.length && !requests.error) return null;
  return (
    <Card title="Changes asked for" padded={false}>
      {requests.error ? (
        <ErrorBanner>{errorMessage(requests.error)}</ErrorBanner>
      ) : (
        <div data-testid="change-requests">
          <Table stack>
            <thead>
              <tr>
                <TH>Asked</TH>
                {everyone && <TH>By</TH>}
                <TH>About</TH>
                <TH>What was asked</TH>
                <TH>Status</TH>
              </tr>
            </thead>
            <tbody>
              {(requests.data ?? []).map((r) => {
                const status = CHANGE_STATUS[r.status];
                return (
                  <tr key={r.id}>
                    <TD primary>{formatDateTime(r.asked_at)}</TD>
                    {everyone && <TD label="By">{r.asked_by.name}</TD>}
                    <TD label="About">
                      {r.card.label}
                      {r.card.kind !== 'view' && (
                        <span className="block text-xs text-slate-500">on {r.view.label}</span>
                      )}
                    </TD>
                    <TD label="What was asked">
                      <span className="whitespace-pre-line">{r.words}</span>
                    </TD>
                    <TD label="Status">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

/** Whether the person's two-factor sign-in is on, and new recovery codes for a current code. */
function TwoFactorCard() {
  const meta = useAppMeta();
  const queryClient = useQueryClient();
  const status = useQuery(trpc.auth.twoFactor.status.queryOptions());
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const regenerate = useMutation({
    mutationFn: () => trpcClient.auth.twoFactor.regenerateRecoveryCodes.mutate({ code }),
    onSuccess: async (result) => {
      setCode('');
      setCodes(result.recovery_codes);
      await queryClient.invalidateQueries({ queryKey: trpc.auth.twoFactor.status.queryKey() });
    },
  });
  const left = status.data?.recovery_codes_left ?? 0;
  return (
    <Card title="Two-factor sign-in">
      {status.isLoading ? (
        <Spinner />
      ) : status.error ? (
        <ErrorBanner>{errorMessage(status.error)}</ErrorBanner>
      ) : status.data?.enabled ? (
        <div className="space-y-4">
          <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <Badge tone="green">On</Badge>
            <span>
              Set up {formatDateTime(status.data.enabled_at)}. {left} unused recovery{' '}
              {left === 1 ? 'code' : 'codes'}.
            </span>
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              regenerate.mutate();
            }}
            className="flex max-w-xl items-end gap-2"
          >
            <div className="flex-1">
              <Label htmlFor="regenerate-code">Code from your authenticator app</Label>
              <Input
                id="regenerate-code"
                required
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={10}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <Button type="submit" busy={regenerate.isPending}>
              <ShieldCheck className="size-4" aria-hidden /> New recovery codes
            </Button>
          </form>
          {regenerate.error && <ErrorBanner>{errorMessage(regenerate.error)}</ErrorBanner>}
          <p className="max-w-3xl text-xs text-slate-500">
            New recovery codes replace the ones you have, which stop working. If you lose your
            phone, sign in with a recovery code, or ask an administrator to reset your two-factor
            sign-in so that you can set it up again.
          </p>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          <Badge tone="amber">Not set up</Badge> You set it up the next time you sign in.
        </p>
      )}
      <Modal
        open={codes !== null}
        onOpenChange={(open) => !open && setCodes(null)}
        title="Your new recovery codes"
        description="Your old codes no longer work. Save these now: this is the only time they are shown."
        footer={<Button onClick={() => setCodes(null)}>Done</Button>}
      >
        <RecoveryCodes codes={codes ?? []} account={meta.user.email} />
      </Modal>
    </Card>
  );
}

function TokensCard() {
  const queryClient = useQueryClient();
  const tokens = useQuery(trpc.auth.tokens.list.queryOptions());
  const [name, setName] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const reload = () =>
    queryClient.invalidateQueries({ queryKey: trpc.auth.tokens.list.queryKey() });
  const create = useMutation({
    mutationFn: () => trpcClient.auth.tokens.create.mutate({ name }),
    onSuccess: async (result) => {
      setCreated(result.token);
      setName('');
      await reload();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => trpcClient.auth.tokens.revoke.mutate({ id }),
    onSuccess: reload,
  });
  return (
    <Card title="API tokens">
      <p className="mb-3 text-sm text-slate-600">
        Integrations call the REST API at <code className="rounded bg-slate-100 px-1">/api/v1</code>{' '}
        with one of your tokens, and can do exactly what you can. The reference is at{' '}
        <a href="/api/v1/openapi.json" className="text-brand-strong hover:underline">
          /api/v1/openapi.json
        </a>
        .
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="mb-4 flex max-w-xl items-end gap-2"
      >
        <div className="flex-1">
          <Label htmlFor="token-name">New token</Label>
          <Input
            id="token-name"
            placeholder="What will use it, such as Field app"
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <Button type="submit" busy={create.isPending}>
          <KeyRound className="size-4" aria-hidden /> Create
        </Button>
      </form>
      {create.error && <ErrorBanner>{errorMessage(create.error)}</ErrorBanner>}
      {revoke.error && <ErrorBanner>{errorMessage(revoke.error)}</ErrorBanner>}
      {tokens.isLoading ? (
        <Spinner />
      ) : (tokens.data ?? []).length === 0 ? (
        <EmptyState title="No tokens yet" />
      ) : (
        <Table stack>
          <thead>
            <tr>
              <TH>Name</TH>
              <TH>Created</TH>
              <TH>Last used</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {(tokens.data ?? []).map((t) => (
              <tr key={t.id}>
                <TD primary>{t.name}</TD>
                <TD label="Created">{formatDateTime(t.created_at)}</TD>
                <TD label="Last used">
                  {t.last_used_at ? formatDateTime(t.last_used_at) : 'Never'}
                </TD>
                <TD align="right">
                  {t.revoked_at ? (
                    <Badge>Revoked</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => revoke.mutate(t.id)}
                      disabled={revoke.isPending}
                    >
                      Revoke
                    </Button>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Modal
        open={created !== null}
        onOpenChange={(open) => !open && setCreated(null)}
        title="Copy your new token"
        description="This is the only time it is shown. Store it where the integration keeps its secrets."
        footer={<Button onClick={() => setCreated(null)}>Done</Button>}
      >
        <div className="flex items-center gap-2">
          <code
            className="flex-1 rounded bg-slate-100 px-2 py-1.5 text-xs break-all"
            data-testid="new-token"
          >
            {created}
          </code>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Copy token"
            onClick={() => void navigator.clipboard?.writeText(created ?? '')}
          >
            <Copy className="size-4" aria-hidden />
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

function UsersCard() {
  const meta = useAppMeta();
  const queryClient = useQueryClient();
  const users = useQuery(trpc.users.list.queryOptions());
  const [inviting, setInviting] = useState(false);
  const [resetting, setResetting] = useState<UserSummary | null>(null);
  const reload = () => queryClient.invalidateQueries({ queryKey: trpc.users.list.queryKey() });
  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active
        ? trpcClient.users.reactivate.mutate({ user_id: id })
        : trpcClient.users.deactivate.mutate({ user_id: id }),
    onSuccess: reload,
  });
  const roleLabel = (role: string) =>
    meta.options['role']?.find((r) => r.value === role)?.label ?? role;
  return (
    <Card
      title="People"
      actions={
        meta.access.writable && (
          <Button size="sm" onClick={() => setInviting(true)}>
            <UserPlus className="size-4" aria-hidden /> Invite
          </Button>
        )
      }
      padded={false}
    >
      {setActive.error && <ErrorBanner>{errorMessage(setActive.error)}</ErrorBanner>}
      {users.isLoading ? (
        <Spinner />
      ) : users.error ? (
        <ErrorBanner>{errorMessage(users.error)}</ErrorBanner>
      ) : (
        <Table stack>
          <thead>
            <tr>
              <TH>Name</TH>
              <TH>Email</TH>
              <TH>Roles</TH>
              <TH>Two-factor</TH>
              <TH>Last signed in</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((u) => (
              <tr key={u.id}>
                <TD primary>
                  {u.name}
                  {u.status !== 'active' && (
                    <span className="ml-2">
                      <Badge tone={u.status === 'invited' ? 'amber' : 'neutral'}>
                        {u.status === 'invited' ? 'Invited' : 'Deactivated'}
                      </Badge>
                    </span>
                  )}
                  {u.locked && (
                    <span className="ml-2">
                      <Badge tone="red">Locked</Badge>
                    </span>
                  )}
                </TD>
                <TD label="Email">{u.email}</TD>
                <TD label="Roles">{u.roles.map(roleLabel).join(', ')}</TD>
                <TD label="Two-factor">
                  {u.two_factor_enabled_at ? (
                    <Badge tone="green">On</Badge>
                  ) : (
                    <Badge>Not set up</Badge>
                  )}
                </TD>
                <TD label="Last signed in">
                  {u.last_login_at ? formatDateTime(u.last_login_at) : 'Never'}
                </TD>
                <TD align="right">
                  {u.id !== meta.user.id && u.two_factor_enabled_at && (
                    <Button size="sm" variant="ghost" onClick={() => setResetting(u)}>
                      Reset two-factor
                    </Button>
                  )}
                  {meta.access.writable && u.id !== meta.user.id && u.status !== 'invited' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={setActive.isPending}
                      onClick={() => setActive.mutate({ id: u.id, active: u.status !== 'active' })}
                    >
                      {u.status === 'active' ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <InviteDialog open={inviting} onClose={() => setInviting(false)} onInvited={reload} />
      <ResetTwoFactorDialog
        user={resetting}
        onClose={() => setResetting(null)}
        onReset={async () => {
          setResetting(null);
          await reload();
        }}
      />
    </Card>
  );
}

/** An administrator turns someone else's two-factor sign-in off, when they have lost their phone. */
function ResetTwoFactorDialog({
  user,
  onClose,
  onReset,
}: {
  user: UserSummary | null;
  onClose: () => void;
  onReset: () => Promise<void>;
}) {
  const reset = useMutation({
    mutationFn: (id: string) => trpcClient.users.resetTwoFactor.mutate({ user_id: id }),
    onSuccess: onReset,
  });
  const close = () => {
    reset.reset();
    onClose();
  };
  return (
    <Modal
      open={user !== null}
      onOpenChange={(open) => !open && close()}
      title={`Reset two-factor sign-in for ${user?.name ?? ''}?`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="danger"
            busy={reset.isPending}
            onClick={() => user && reset.mutate(user.id)}
          >
            Reset two-factor sign-in
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-slate-600">
        {reset.error && <ErrorBanner>{errorMessage(reset.error)}</ErrorBanner>}
        <p>
          Their authenticator app and recovery codes stop working and they are signed out
          everywhere. The next time they sign in with their password, they set up two-factor sign-in
          again. Their API tokens keep working.
        </p>
        <p>
          Anyone who knows their password could set it up in their place, so reset it only when you
          are sure the request comes from them.
        </p>
      </div>
    </Modal>
  );
}

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => Promise<void>;
}) {
  const meta = useAppMeta();
  const roles = meta.options['role'] ?? [];
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => trpcClient.users.invite.mutate({ email, name, roles: chosen }),
    onSuccess: async (result) => {
      setLink(result.link);
      await onInvited();
    },
  });
  const close = () => {
    setEmail('');
    setName('');
    setChosen([]);
    setLink(null);
    invite.reset();
    onClose();
  };
  const toggle = (role: string) =>
    setChosen((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    );
  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && close()}
      title={link ? 'Invitation sent' : 'Invite someone'}
      footer={
        link ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={invite.isPending}
              disabled={!email || !name || chosen.length === 0}
              onClick={() => invite.mutate()}
            >
              Send invitation
            </Button>
          </>
        )
      }
    >
      {link ? (
        <div className="space-y-2 text-sm text-slate-600">
          <p>
            {email} has been emailed a link to choose a password. It works for seven days. If the
            email does not arrive, pass them this link:
          </p>
          <code className="block rounded bg-slate-100 px-2 py-1.5 text-xs break-all">{link}</code>
        </div>
      ) : (
        <div className="space-y-4">
          {invite.error && <ErrorBanner>{errorMessage(invite.error)}</ErrorBanner>}
          <div>
            <Label htmlFor="invite-name" required>
              Name
            </Label>
            <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="invite-email" required>
              Email
            </Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-slate-700">Roles</legend>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {roles.map((role) => (
                <label key={role.value} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-slate-300"
                    checked={chosen.includes(role.value)}
                    onChange={() => toggle(role.value)}
                  />
                  {role.label}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </Modal>
  );
}

const SOURCE_BADGE: Record<
  SourceSummary['state'],
  { tone: 'green' | 'amber' | 'red' | 'neutral'; text: string }
> = {
  fresh: { tone: 'green', text: 'up to date' },
  late: { tone: 'amber', text: 'late' },
  failing: { tone: 'red', text: 'failing' },
  never: { tone: 'neutral', text: 'not synced yet' },
};

type SourceSummary = RouterOutputs['dashboard']['sources'][number];

/**
 * Where the owner dashboard's data comes from, and the sample business for demonstrations.
 * Loading a sample replaces one already there; a business's own data is never mixed with it.
 */
function DashboardDataCard() {
  const meta = useAppMeta();
  const queryClient = useQueryClient();
  const sources = useQuery(trpc.dashboard.sources.queryOptions());
  const [confirm, setConfirm] = useState<'load' | 'clear' | null>(null);
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: appMetaQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: trpc.dashboard.pathKey() }),
    ]);
  };
  const load = useMutation({
    mutationFn: () => trpcClient.dashboard.loadSample.mutate(),
    onSuccess: async () => {
      await refresh();
      setConfirm(null);
    },
  });
  const clear = useMutation({
    mutationFn: () => trpcClient.dashboard.clearSample.mutate(),
    onSuccess: async () => {
      await refresh();
      setConfirm(null);
    },
  });
  const list = sources.data ?? [];
  const sample = list.some((s) => s.sample);
  const own = list.some((s) => !s.sample);
  const zone = meta.organization.timezone;
  const close = () => {
    load.reset();
    clear.reset();
    setConfirm(null);
  };
  return (
    <Card title="Dashboard data">
      <p className="mb-3 max-w-3xl text-sm text-slate-600">
        The owner dashboard reads copies of what the business’s own tools hold. Each source is
        listed with when it last synced. Sample data is for demonstrations, and never sits beside a
        business’s own.
      </p>
      {sources.isLoading && <Spinner />}
      {sources.error && <ErrorBanner>{errorMessage(sources.error)}</ErrorBanner>}
      {list.length > 0 && (
        <ul className="mb-4 space-y-1.5 text-sm">
          {list.map((s) => {
            const badge = SOURCE_BADGE[s.state];
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-2">
                <span className="text-slate-900">{s.label}</span>
                {s.sample && <Badge tone="amber">Sample data</Badge>}
                <Badge tone={badge.tone}>{badge.text}</Badge>
                <span className="text-slate-500">
                  {s.last_success_at
                    ? `last synced ${formatDateTime(s.last_success_at, zone)}`
                    : 'never synced'}
                </span>
                {s.last_error && <span className="text-red-700">{s.last_error}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {!sources.isLoading && list.length === 0 && (
        <p className="mb-4 text-sm text-slate-500">No sources yet.</p>
      )}
      {!own && (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={() => setConfirm('load')}>
            {sample ? 'Load a fresh sample' : 'Load sample data'}
          </Button>
          {sample && <Button onClick={() => setConfirm('clear')}>Remove sample data</Button>}
        </div>
      )}
      <Modal
        open={confirm === 'load'}
        onOpenChange={(open) => !open && close()}
        title={sample ? 'Load a fresh sample?' : 'Load sample data?'}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" busy={load.isPending} onClick={() => load.mutate()}>
              {sample ? 'Load a fresh sample' : 'Load sample data'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600">
          {load.error && <ErrorBanner>{errorMessage(load.error)}</ErrorBanner>}
          <p>
            This fills the dashboard with an invented business, dated to today, so it can be shown
            without anyone’s real numbers. Every screen marks it as sample data.
            {sample && ' The sample already here is replaced.'}
          </p>
          <p>It takes a few seconds.</p>
        </div>
      </Modal>
      <Modal
        open={confirm === 'clear'}
        onOpenChange={(open) => !open && close()}
        title="Remove the sample data?"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="danger" busy={clear.isPending} onClick={() => clear.mutate()}>
              Remove sample data
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600">
          {clear.error && <ErrorBanner>{errorMessage(clear.error)}</ErrorBanner>}
          <p>
            Every sample record goes, and the dashboard is empty until the business’s own tools are
            connected or the sample is loaded again.
          </p>
        </div>
      </Modal>
    </Card>
  );
}

function ExportCard() {
  const history = useQuery(trpc.exports.list.queryOptions());
  const queryClient = useQueryClient();
  const run = useMutation({
    mutationFn: async () => {
      const response = await fetch('/downloads/data-export', {
        method: 'POST',
        headers: { 'x-databink-csrf': '1' },
      });
      if (!response.ok)
        throw new Error(`The export failed (${response.status}). ${await response.text()}`);
      const name =
        /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
        'databink-export.zip';
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.exports.list.queryKey() }),
  });
  return (
    <Card title="Your data">
      <p className="mb-3 max-w-3xl text-sm text-slate-600">
        Download everything this organization holds as one ZIP: every record including archived
        ones, comments, files, the audit trail and readable configuration, with a manifest and a
        data dictionary that explain each file. Each export is recorded below. One is also taken on
        a schedule, weekly unless your administrator changed it, and the latest scheduled exports
        stay here to download.
      </p>
      <Button onClick={() => run.mutate()} busy={run.isPending}>
        <Download className="size-4" aria-hidden /> Export all data
      </Button>
      {run.error && (
        <div className="mt-3">
          <ErrorBanner>{errorMessage(run.error)}</ErrorBanner>
        </div>
      )}
      {(history.data ?? []).length > 0 && (
        <div className="mt-4">
          <Table stack>
            <thead>
              <tr>
                <TH>Taken</TH>
                <TH>By</TH>
                <TH align="right">Records</TH>
                <TH>Checksum (SHA-256)</TH>
                <TH />
              </tr>
            </thead>
            <tbody>
              {(history.data ?? []).map((e) => (
                <tr key={e.id}>
                  <TD primary>{formatDateTime(e.created_at)}</TD>
                  <TD label="By">{e.scheduled ? 'Scheduled' : (e.requested_by ?? 'System')}</TD>
                  <TD align="right" label="Records">
                    {e.records.toLocaleString()}
                  </TD>
                  <TD label="Checksum (SHA-256)">
                    <code className="text-xs">{e.sha256.slice(0, 16)}…</code>
                  </TD>
                  <TD align="right">
                    {e.scheduled && (
                      <a
                        href={`/downloads/data-export/${e.id}`}
                        className="font-medium text-brand-strong hover:underline"
                      >
                        Download
                      </a>
                    )}
                  </TD>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function AboutCard() {
  const meta = useAppMeta();
  const { extensions } = meta;
  const items: [string, string[]][] = [
    ['Custom entities', extensions.entities],
    ['Custom fields', extensions.custom_fields],
    ['Hooks', extensions.hooks.map((h) => `${h.point}: ${h.description}`)],
    ['Overrides', extensions.overrides.map((o) => `${o.point}: ${o.reason}`)],
    ['Screen slots', extensions.slots],
    ['Background jobs', extensions.jobs.map((j) => `${j.key}: ${j.description}`)],
    ['Inbound endpoints', extensions.inbound.map((e) => `${e.path}: ${e.description}`)],
  ];
  return (
    <Card title="About this system">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[12rem_1fr]">
        <dt className="text-slate-500">Core</dt>
        <dd>{meta.core_version}</dd>
        {meta.client && (
          <>
            <dt className="text-slate-500">Client package</dt>
            <dd>
              {meta.client.name} ({meta.client.slug}) {meta.client.version}
            </dd>
          </>
        )}
        {items
          .filter(([, values]) => values.length > 0)
          .map(([label, values]) => (
            <div key={label} className="contents">
              <dt className="text-slate-500">{label}</dt>
              <dd>
                <ul className="space-y-0.5">
                  {values.map((v) => (
                    <li key={v}>
                      <code className="text-xs">{v}</code>
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ))}
      </dl>
    </Card>
  );
}
